import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import api from "../lib/api";
import { fmtRelative } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { AlertTriangle, Info, CheckCircle2, Settings, ExternalLink, BellRing } from "lucide-react";
import { DataPanel, EmptyState, MetricStrip, Surface } from "../components/CommandSystem";

const ICONS = {
  warning: { Icon: AlertTriangle, cls: "bg-amber-50 text-amber-600 border-amber-200" },
  info: { Icon: Info, cls: "bg-blue-50 text-blue-600 border-blue-200" },
  success: { Icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-600 border-emerald-200" },
  permission_request: { Icon: Info, cls: "bg-blue-50 text-blue-600 border-blue-200" },
};

const NOTIFICATION_OPTIONS = [
  { key: "campaign_expiring", label: "Expiring campaigns", desc: "Grouped by expiry date and branch." },
  { key: "hoarding_freeing", label: "Hoardings freeing up", desc: "Grouped availability created by campaign endings." },
  { key: "available_inventory", label: "Large availability changes", desc: "Only when enough inventory is free to require action." },
  { key: "payment_due", label: "Campaign payments due", desc: "Grouped receivable reminders for Work Queue." },
  { key: "rent_due", label: "Rent due", desc: "Grouped rent payable reminders for Work Queue." },
  { key: "proposal_activity", label: "Proposal activity", desc: "Proposal workflow and sharing alerts." },
  { key: "permission_request", label: "Permission requests", desc: "Employee access request notifications." },
  { key: "subscription_expiring", label: "Subscription expiry", desc: "Super admin advertiser subscription reminders." },
  { key: "accounting_reconciliation", label: "Accounting reconciliation", desc: "Accounting periods, exports, and reconciliation alerts." },
];

export function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [view, setView] = useState("notifications");
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [pushConfig, setPushConfig] = useState(null);
  const [pushState, setPushState] = useState(() => (typeof Notification === "undefined" ? "unsupported" : Notification.permission));
  const [savingPush, setSavingPush] = useState(false);

  const load = () => api.get("/notifications", { skipGlobalLoader: true }).then((r) => setItems(Array.isArray(r.data) ? r.data : []));
  const loadPrefs = () => api.get("/notifications/preferences", { skipGlobalLoader: true }).then((r) => setPrefs(r.data || null));
  const loadPushConfig = () => api.get("/notifications/push/config", { skipGlobalLoader: true }).then((r) => setPushConfig(r.data || null)).catch(() => setPushConfig(null));

  useEffect(() => {
    load();
    loadPrefs();
    loadPushConfig();
  }, []);

  const notifications = Array.isArray(items) ? items : [];
  const unread = notifications.filter((item) => !item.read).length;
  const enabledCategories = Object.values(prefs?.categories || {}).filter((value) => value !== false).length || NOTIFICATION_OPTIONS.length;

  async function markAllRead() {
    await api.put("/notifications/read-all");
    await load();
    window.dispatchEvent(new Event("hx:notifications-updated"));
  }

  async function markRead(id) {
    setItems((current) => current.map((item) => item.id === id ? { ...item, read: true } : item));
    await api.put(`/notifications/${id}/read`);
    window.dispatchEvent(new Event("hx:notifications-updated"));
  }

  function setCategory(key, value) {
    setPrefs((current) => ({
      ...(current || {}),
      categories: { ...((current || {}).categories || {}), [key]: value },
    }));
  }

  async function savePrefs() {
    setSavingPrefs(true);
    try {
      const payload = {
        categories: prefs?.categories || {},
        days_ahead: Number(prefs?.days_ahead || 7),
      };
      const { data } = await api.put("/notifications/preferences", payload);
      setPrefs(data);
      await load();
      window.dispatchEvent(new Event("hx:notifications-updated"));
    } finally {
      setSavingPrefs(false);
    }
  }

  async function openNotification(n) {
    if (!n.read) await markRead(n.id);
    if (n.action_url) navigate(n.action_url);
  }

  function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
  }

  async function enablePush() {
    if (!pushConfig?.enabled || !pushConfig.vapid_public_key) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || typeof Notification === "undefined") {
      setPushState("unsupported");
      return;
    }
    setSavingPush(true);
    try {
      const permission = await Notification.requestPermission();
      setPushState(permission);
      if (permission !== "granted") return;
      const registration = await navigator.serviceWorker.register("/hoardexa-sw.js");
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(pushConfig.vapid_public_key),
      });
      await api.post("/notifications/push/subscriptions", subscription.toJSON());
      setPushState("granted");
    } finally {
      setSavingPush(false);
    }
  }

  return (
    <Layout
      title="Notifications"
      subtitle="Actionable operations inbox for renewals, receivables, payables, permission requests, and business exceptions."
      actions={
        <div className="flex items-center justify-end gap-1.5 whitespace-nowrap sm:gap-2">
          <Button size="sm" variant={view === "settings" ? "default" : "outline"} onClick={() => setView((v) => v === "settings" ? "notifications" : "settings")}>
            <Settings size={14} /> <span className="hidden sm:inline">Manage</span>
          </Button>
          {unread > 0 && (
            <Button size="sm" variant="outline" onClick={markAllRead}>
              <CheckCircle2 size={14} />
              <span className="hidden sm:inline">Mark all as read</span>
              <span className="sm:hidden">Read</span>
            </Button>
          )}
        </div>
      }
    >
      <MetricStrip
        metrics={[
          { label: "Unread", value: unread, hint: "Needs attention", tone: unread ? "rose" : "emerald" },
          { label: "Total visible", value: notifications.length, hint: "Stored notification records", tone: "blue" },
          { label: "Enabled categories", value: `${enabledCategories}/${NOTIFICATION_OPTIONS.length}`, hint: `${prefs?.days_ahead ?? 7} day reminder window`, tone: "indigo" },
        ]}
      />

      {view === "settings" && (
        <DataPanel title="Notification management" subtitle="Choose which alerts are generated for your branch scope." className="mb-4 max-w-5xl">
          <div className="grid gap-4 lg:grid-cols-[1fr_220px]">
            <div>
              <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-white text-blue-700">
                    <BellRing size={15} />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-950">Browser push</div>
                    <p className="mt-1 text-xs leading-5 text-slate-600">
                      When enabled, useful notifications can reach this browser even when Hoardexa is closed. Repeated alerts are deduped by event.
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Button size="sm" onClick={enablePush} disabled={savingPush || !pushConfig?.enabled || pushState === "granted"} className="bg-blue-600 text-white hover:bg-blue-700">
                        {pushState === "granted" ? "Push enabled" : savingPush ? "Enabling..." : "Enable push"}
                      </Button>
                      <span className="text-xs font-semibold text-slate-500">
                        {!pushConfig?.enabled ? "Server push keys are not configured." : pushState === "denied" ? "Permission blocked in browser." : pushState === "unsupported" ? "Browser not supported." : `Browser permission: ${pushState}`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Future reminder window</label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={60}
                  value={prefs?.days_ahead ?? 7}
                  onChange={(event) => setPrefs((current) => ({ ...(current || {}), days_ahead: event.target.value }))}
                />
                <span className="text-xs text-slate-500">days</span>
              </div>
            </div>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {NOTIFICATION_OPTIONS.map((option) => (
              <div key={option.key} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{option.label}</div>
                    <div className="mt-1 text-xs leading-5 text-slate-500">{option.desc}</div>
                  </div>
                  <Switch
                    checked={prefs?.categories?.[option.key] !== false}
                    onCheckedChange={(checked) => setCategory(option.key, checked)}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex justify-end">
            <Button onClick={savePrefs} disabled={savingPrefs} className="bg-blue-600 hover:bg-blue-700 text-white">
              {savingPrefs ? "Saving..." : "Save preferences"}
            </Button>
          </div>
        </DataPanel>
      )}

      <Surface className="hx-notifications-surface max-w-5xl p-3" data-testid="notifications-list">
        {notifications.length === 0 ? (
          <EmptyState title="All caught up" description="No notifications are waiting for this branch scope." />
        ) : (
          <ul className="hx-notification-grid">
            {notifications.map((n) => {
              const cfg = ICONS[n.type] || ICONS.info;
              const Icon = cfg.Icon;
              return (
                <li key={n.id} className={`hx-notification-tile ${n.read ? "" : "is-unread"}`}>
                  <div className={`hx-notification-icon ${cfg.cls}`}>
                    <Icon size={16} />
                  </div>
                  <div className="hx-notification-body">
                    <div className="hx-notification-title-row">
                      <div className="hx-notification-title">{n.title}</div>
                      {!n.read && <span className="hx-notification-dot" />}
                    </div>
                    <div className="hx-notification-message">{n.message}</div>
                    <div className="hx-notification-meta">
                      <span>{fmtRelative(n.created_at)}</span>
                      {n.category && <span>{n.category.replace(/_/g, " ")}</span>}
                    </div>
                  </div>
                  <div className="hx-notification-actions">
                    {n.action_url && (
                      <button type="button" onClick={() => openNotification(n)}>
                        View <ExternalLink size={12} />
                      </button>
                    )}
                    {!n.read && (
                      <button type="button" onClick={() => markRead(n.id)}>
                        Mark read
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Surface>
    </Layout>
  );
}
